import { Component, OnInit } from '@angular/core';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';
import { AuthService } from 'src/app/services/auth/auth.service';

/**
 * PARÁMETROS → BANDAS DE APROBACIÓN (4.6c; maqueta vistas 11 y 12; Mario 08-oct).
 * Quién aprueba cada tanda según su monto no está en el código: es esta configuración VERSIONADA.
 * La gerente propone (topes y quién firma, una o dos firmas, desde una fecha); el director ve qué cambia y la
 * aprueba o la devuelve. Las reglas las valida el núcleo; aquí se arma y se muestra. Rangos reales: P24.
 */
@Component({
  selector: 'app-bandas',
  templateUrl: './bandas.component.html'
})
export class BandasComponent implements OnInit {
  cargando = true;
  error = '';
  aviso = '';
  vigente: any = null;
  versiones: any[] = [];

  nueva: { desde: string; motivo: string; bandas: { hasta: number | null; f0: string; f1: string }[] } | null = null;
  enviando = false;
  observacion = '';
  devolviendo: string | null = null;

  readonly ROLES = [{ v: 'TES', t: 'Tesorero' }, { v: 'GER', t: 'Gerente de finanzas' }, { v: 'DIR', t: 'Director' }];
  readonly ROL: any = { TES: 'Tesorero', GER: 'Gerente de finanzas', DIR: 'Director' };

  constructor(private liq: LiquidacionService, public _auth: AuthService) { }

  ngOnInit(): void { this.cargar(); }

  get puedeProponer(): boolean { return this._auth.hasPermission('config.bandas.propose'); }
  get puedeAprobar(): boolean { return this._auth.hasPermission('config.bandas.approve'); }
  get propuestas(): any[] { return this.versiones.filter((v) => v.estado === 'PROPUESTA'); }

  cargar = async (): Promise<void> => {
    this.cargando = true; this.error = '';
    try { const r = await this.liq.bandas(); this.vigente = r?.vigente || null; this.versiones = r?.versiones || []; }
    catch (e: any) { this.error = e?.error?.msg || 'No se pudieron leer las bandas.'; }
    finally { this.cargando = false; }
  };

  /** Arranca con la vigente (o una de ejemplo si no hay ninguna). */
  empezar = (): void => {
    const base = this.vigente?.bandas || [{ hasta: 5000, firmas: ['TES'] }, { hasta: 25000, firmas: ['GER'] }, { hasta: 100000, firmas: ['DIR'] }, { hasta: null, firmas: ['GER', 'DIR'] }];
    this.nueva = { desde: this.manana(), motivo: '', bandas: base.map((b: any) => ({ hasta: b.hasta, f0: b.firmas[0], f1: b.firmas[1] || '' })) };
  };
  agregarBanda = (): void => {
    if (!this.nueva) return;
    const ult = this.nueva.bandas[this.nueva.bandas.length - 1];
    const tope = this.nueva.bandas.length > 1 ? Number(this.nueva.bandas[this.nueva.bandas.length - 2].hasta) * 2 : 1000;
    this.nueva.bandas.splice(this.nueva.bandas.length - 1, 0, { hasta: tope, f0: ult.f0, f1: '' });
  };
  quitarBanda = (i: number): void => { if (this.nueva && this.nueva.bandas.length > 1 && i < this.nueva.bandas.length - 1) this.nueva.bandas.splice(i, 1); };

  enviar = async (): Promise<void> => {
    if (!this.nueva) return;
    this.enviando = true; this.error = ''; this.aviso = '';
    const bandas = this.nueva.bandas.map((b, i, a) => ({ hasta: i === a.length - 1 ? null : Number(b.hasta), firmas: [b.f0, b.f1].filter(Boolean) }));
    try {
      const v = await this.liq.proponerBandas({ desde: this.nueva.desde, motivo: this.nueva.motivo.trim() || undefined, bandas });
      this.aviso = `Propuesta ${v.code} enviada al director. Mientras tanto rige ${this.vigente?.code || 'ninguna'}.`;
      this.nueva = null;
      await this.cargar();
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo proponer la versión.'; }
    finally { this.enviando = false; }
  };

  aprobar = async (v: any): Promise<void> => {
    this.error = ''; this.aviso = '';
    try { await this.liq.aprobarBandas(v.code); this.aviso = `${v.code} aprobada: rige desde el ${this.fecha(v.desde)}.`; await this.cargar(); }
    catch (e: any) { this.error = e?.error?.msg || 'No se pudo aprobar.'; }
  };
  devolver = async (v: any): Promise<void> => {
    if (!this.observacion.trim()) { this.error = 'Para devolverla escribe la observación.'; return; }
    try { await this.liq.devolverBandas(v.code, this.observacion.trim()); this.aviso = `${v.code} devuelta a la gerente.`; this.devolviendo = null; this.observacion = ''; await this.cargar(); }
    catch (e: any) { this.error = e?.error?.msg || 'No se pudo devolver.'; }
  };

  // ── para leer ──
  rango = (bs: any[], i: number): string => {
    const desde = i === 0 ? 0 : bs[i - 1].hasta;
    const f = (n: number) => `$ ${Number(n).toLocaleString('es-VE')}`;
    return bs[i].hasta === null ? `más de ${f(desde)}` : `${i === 0 ? 'hasta' : `de ${f(desde)} a`} ${f(bs[i].hasta)}`;
  };
  firmas = (b: any): string => (b.firmas || []).map((r: string) => this.ROL[r]).join(' y luego ');
  /** La versión que esta propuesta reemplaza: la última APROBADA que rige antes de su fecha (no la de hoy:
   *  puede haber una aprobada que todavía no empezó). */
  anterior = (v: any): any => this.versiones
    .filter((x) => x.estado === 'APROBADA' && x.code !== v.code && x.desde <= v.desde)
    .sort((a, b) => (a.desde < b.desde ? 1 : -1))[0] || null;

  /** Antes → ahora, banda por banda, contra la versión que reemplaza. */
  cambia = (v: any, i: number): boolean => {
    const ant = this.anterior(v), a = ant?.bandas?.[i];
    // Se compara el RANGO completo: si baja el tope de una banda, cambia también el «desde» de la siguiente.
    return !a || this.rango(ant.bandas, i) !== this.rango(v.bandas, i) || (a.firmas || []).join() !== (v.bandas[i].firmas || []).join();
  };
  estado = (v: any): { t: string; c: string } => ({
    APROBADA: { t: 'Aprobada', c: 'kb-badge-exito' }, PROPUESTA: { t: 'Por aprobar', c: 'kb-badge-alerta' }, DEVUELTA: { t: 'Devuelta', c: 'kb-badge-neutro' },
  } as any)[v.estado] || { t: v.estado, c: 'kb-badge-neutro' };

  private manana(): string { const d = new Date(Date.now() - 4 * 3600e3 + 864e5); return d.toISOString().slice(0, 10); }

  /** 2026-10-08 → 08/10/2026. */
  fecha = (iso: string | null): string => (iso ? iso.split('-').reverse().join('/') : '—');
}
